import { HttpClient } from '@angular/common/http';
import {
  Component,
  input,
  output,
  effect,
  signal,
  ViewEncapsulation,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
  ChangeDetectionStrategy,
  DestroyRef,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Message, Participant } from '@mitel/cloudlink-sdk/chat';
import { getItemsFromCollection } from '@mitel/cloudlink-sdk/utils';
import findIndex from 'lodash-es/findIndex';

import { ChatAuthService } from '../../services/chat-auth.service';
import { ChatCloudLinkService } from '../../services/chat-cloudlink.service';
import { SharedHelpers } from '../../classes/sharedhelpers';
import { ScrollingService } from '../../services/scrolling.service';
import { ChatConversationService } from '../../services/chat-conversation.service';
import { ChatLoggingService } from '../../services/chat-logging.service';
import { Subscription, Subject, timer } from 'rxjs';
import { map, filter, distinctUntilChanged, take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { trigger, transition, style, animate } from '@angular/animations';

export interface SuggestionMessage {
  body: any;
  messageId: string;
  createdOn: Date;
  isStartOfDay: boolean;
  suggestions?: any;
  archived: boolean;
  suggestFaqAnswersResponse?: any;
  suggestArticlesResponse?: any;
  articleExpanded?: boolean;
  faqExpanded?: boolean;
}

@Component({
  selector: 'chat-suggestion',
  templateUrl: './chat-suggestion.component.html',
  styleUrls: ['./chat-suggestion.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  providers: [ScrollingService],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [style({ opacity: 0 }), animate(500, style({ opacity: 1 }))]),
      transition(':leave', [animate(500, style({ opacity: 0 }))])
    ])
  ],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatSuggestionComponent implements OnDestroy, AfterViewInit {
  private conversationId: string;

  locale = input('en');
  parentConversationId = input.required<string>();
  useStubData = input(false);
  findMessageInput = input<string>();

  goToChatMessage = output<string>();

  @ViewChild('activesuggestionscontent') activesuggestionscontent: ElementRef;

  private subs: Subscription = new Subscription();
  private next: any = {
    active: { $skip: 0, $top: 50 },
    archive: { $skip: 0, $top: 50 }
  };
  private defaultNext = { $skip: 0, $top: 50 };
  private moreMessages = {
    active: false,
    archive: false
  };
  private scrollingSubUp: Subscription;
  private scrollingSubDown: Subscription;
  private fetchingMessages = {
    active: false,
    archive: false
  };
  private dateTracker = {
    active: {},
    archive: {}
  };
  private isUserParticpantOfConversation = signal(false);
  private destroyRef = inject(DestroyRef);
  private chatLoggingService = inject(ChatLoggingService);
  private participantCheckSub: Subscription | null = null;
  private readySub: Subscription;
  private isFocused = signal(false);

  public scrollIndexSubject = new Subject<any>();
  public error = signal('');
  public selectedTabIndex = signal(0);
  public loading = signal(true);
  public unReadMessages = signal(0);
  public suggestions = signal<SuggestionMessage[]>([]);

  constructor(
    private chatCloudLinkService: ChatCloudLinkService,
    private chatAuthService: ChatAuthService,
    private chatConversationService: ChatConversationService,
    private scrollingService: ScrollingService,
    private httpClient: HttpClient,
    public translateService: TranslateService
  ) {
    // Watch for findMessageInput changes
    effect(() => {
      const messageId = this.findMessageInput();
      if (messageId) {
        this.clearError();
        this.findMessage(messageId);
      }
    });

    // Watch for parentConversationId changes
    effect(() => {
      const convId = this.parentConversationId();
      if (convId) {
        if (!this.useStubData() && !this.isGuid(convId)) {
          this.chatLoggingService.error({
            logMessage: 'Invalid parentConversationId - NOT GUID:',
            logParameters: [convId]
          });
          return;
        }
        this.initialize();
      }
    });
  }

  ngAfterViewInit() {
    this.setupScrolling('active');
  }

  public initialize() {
    if (this.readySub) {
      this.readySub.unsubscribe();
    }
    this.participantCheckSub?.unsubscribe();
    this.participantCheckSub = null;
    this.readySub = this.chatAuthService.ready$.subscribe(
      async () => {
        try {
          this.clearError();
          if (this.parentConversationId) {
            await this.fetchSuggestionConversationId();
            this.subscribeParticipantChange();
            await this.checkIfUserIsParticipant();

            this.participantCheckSub = timer(0, 500)
              .pipe(
                take(10),
                takeUntilDestroyed(this.destroyRef),
                filter(() => this.isUserParticpantOfConversation()),
                take(1)
              )
              .subscribe(() => {
                this.participantCheckSub = null;
                this.suggestions.set([]);
                this.next.active = JSON.parse(JSON.stringify(this.defaultNext));
                this.fetchSuggestionMessages('active').then(() => {
                  setTimeout(() => this.scrollingService.scrollToBottom(), 0);
                });
                this.subscribeMessages();
              });
          }
        } catch (error) {
          this.error.set('ai_suggestions.errors.failed_to_load');
          this.loading.set(false);
          this.chatLoggingService.error({
            logMessage: 'chat-suggestion.component.ngOnChanges()',
            logParameters: [error]
          });
        }
      },
      er => {
        //Let chat component handle error
      }
    );
  }

  public onGoToChatMessage(messageId: string) {
    this.goToChatMessage.emit(messageId);
  }

  public async findMessage(messageId: string) {
    let elementId;
    const suggestion = this.suggestions().find(s => s.body.parentMessageId === messageId);
    if (suggestion) {
      if (suggestion.archived) {
        elementId = messageId + '-archive';
      } else {
        elementId = messageId + '-active';
      }

      setTimeout(() => {
        if (document.getElementById(elementId)) {
          this.scrollingService.scrollToMessage(elementId);
        }
      }, 0);
    } else {
      if (this.moreMessages['active']) {
        await this.fetchSuggestionMessages('active');

        setTimeout(() => {
          this.findMessage(messageId);
        }, 0);
      }
    }
  }

  public scrollToBottom() {
    this.scrollingService.scrollToBottom();
    this.unReadMessages.set(0);
  }

  private subscribeParticipantChange() {
    this.subs.add(
      this.chatCloudLinkService.participantsChanged$.pipe(map(event => <Participant>event)).subscribe(resource => {
        try {
          const participant = resource as Participant;
          if (
            participant.conversationId === this.conversationId &&
            participant.userId === this.chatAuthService.userId
          ) {
            this.isUserParticpantOfConversation.set(true);
          }
        } catch (ex) {
          // stifle exception to prevent unsubscribing
          this.chatLoggingService.error({
            logMessage: 'chat.component.subscribeParticipantChange(): Error in subscribe ParticipantChange',
            logParameters: [ex]
          });
        }
      })
    );
  }

  private async checkIfUserIsParticipant() {
    if (this.useStubData()) {
      this.isUserParticpantOfConversation.set(true);
      return;
    }
    try {
      await this.chatCloudLinkService.chat.getParticipant({
        conversationId: this.conversationId,
        participantId: this.chatAuthService.userId
      });
      this.isUserParticpantOfConversation.set(true);
    } catch (e) {
      this.chatLoggingService.error({
        logMessage: `Error while checking if user ${this.chatAuthService.userId} is participant of conversation ${this.conversationId} :: ${e}`
      });
      throw e;
    }
  }

  private isGuid(value: string) {
    const regexGuid = /^[0-9a-fA-F]{8}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{12}$/gi;
    return regexGuid.test(value);
  }

  private subscribeMessages(): void {
    this.chatLoggingService.info({
      logMessage: 'chat-suggestion.component.subscribeMessages() for ',
      logParameters: [this.conversationId]
    });
    this.subs.add(
      this.chatCloudLinkService.messagesChanged$
        .pipe(
          map(event => <Message>event),
          filter(msg => msg.conversationId === this.conversationId),
          distinctUntilChanged((p, c) => p.messageId === c.messageId && p.body === c.body)
        )
        .subscribe(resource => {
          try {
            this.onMessageReceived(resource);
          } catch (ex) {
            // stifle exception to prevent unsubscribing
            this.chatLoggingService.error({
              logMessage: 'chat-suggestion.component.ngOnInit(): Error in subscribe messageReceived',
              logParameters: [ex]
            });
          }
        })
    );
  }

  private onMessageReceived(message: Message) {
    const currentSuggestions = this.suggestions();
    const i: number = findIndex(currentSuggestions, (m: SuggestionMessage) => m.messageId === message.messageId);
    if (i === -1) {
      const body = JSON.parse(message.body);
      const type = message.tag && message.tag.toLowerCase() === 'archived' ? 'archive' : 'active';
      const newSuggestions = [
        ...currentSuggestions,
        {
          body: body,
          messageId: message.messageId,
          createdOn: message.createdOn,
          isStartOfDay: this.isStartOfDay(message.createdOn, type),
          archived: type === 'archive',
          articleExpanded: false,
          faqExpanded: false
        }
      ];
      this.suggestions.set(
        newSuggestions.sort((a, b) => {
          return new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime();
        })
      );
      if (!this.isFocused() || !this.scrollingService.isNearBottom()) {
        this.unReadMessages.update(count => count + 1);
      }
    }
  }

  ngOnDestroy() {
    this.chatLoggingService.info({
      logMessage: 'chat-suggestion.component.ngOnDestroy() ',
      logParameters: [this.conversationId]
    });
    this.subs.unsubscribe();
    if (this.readySub) {
      this.readySub.unsubscribe();
    }
  }

  public clearError() {
    this.error.set('');
  }

  private async fetchSuggestionConversationId() {
    if (this.useStubData()) {
      // Stub mode: use parent conversation ID as suggestion conversation ID
      this.conversationId = this.parentConversationId() + '-suggestions';
      this.chatLoggingService.info({
        logMessage: '[STUB] Using stub suggestion conversationId:',
        logParameters: [this.conversationId]
      });
      return;
    }

    if (this.parentConversationId()) {
      const res = await this.chatCloudLinkService.chat.getTag({
        conversationId: this.parentConversationId(),
        tagId: 'suggestionsConversationId'
      });
      const tag = res.body;
      if (tag && tag.value) {
        this.chatLoggingService.info({ logMessage: 'fetched suggestion conversationId:', logParameters: [tag.value] });
        this.conversationId = tag.value;
      }
    }
  }

  private async fetchSuggestionMessages(searchType: string) {
    this.clearError();
    this.fetchingMessages[searchType] = true;

    if (this.useStubData()) {
      // Stub mode: return mock suggestions
      await this.loadStubSuggestions(searchType);
      this.fetchingMessages[searchType] = false;
      this.loading.set(false);
      return;
    }

    try {
      this.chatLoggingService.info({
        logMessage: `chat-suggestion.component.fetchSuggestionMessages() ${searchType} fetch start `,
        logParameters: [this.conversationId]
      });
      const params: any = { conversationId: this.conversationId };

      params.skip = this.next[searchType].$skip;
      params.skipToken = this.next[searchType].$skipToken;
      params.top = this.next[searchType].$top;
      const res = await this.chatCloudLinkService.chat.getMessages(params);
      const messageCollection = res.body;
      this.moreMessages[searchType] =
        messageCollection && messageCollection._links && messageCollection._links.next ? true : false;
      if (this.moreMessages[searchType]) {
        const next: string = messageCollection._links.next || '';
        const queryString: any = SharedHelpers.parseQueryString(next);
        this.next[searchType].$skip = queryString.$skip;
        this.next[searchType].$skipToken = queryString.$skipToken;
        this.next[searchType].$top = queryString.$top;
        this.next[searchType].$search = queryString.$search;
        this.next[searchType].$filter = queryString.$filter;
      }
      const messages = getItemsFromCollection(messageCollection).sort(
        (a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime()
      );

      const currentSuggestions = this.suggestions();
      const newItems = messages.map(m => {
        const body = JSON.parse(m.body);
        return {
          body: body,
          messageId: m.messageId,
          createdOn: m.createdOn,
          isStartOfDay: false,
          archived: m.tag && m.tag.toLowerCase() === 'archived' ? true : false,
          articleExpanded: false,
          faqExpanded: false
        };
      });

      const combinedSuggestions = [...currentSuggestions, ...newItems];
      const sortedSuggestions = combinedSuggestions.sort((a, b) => {
        return new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime();
      });

      // remove hidden items and duplicates. Duplicates can happen if we get a notification
      // of a message before we finish loading this request, that is also returned by the call to messages
      const uniqueSuggestions = sortedSuggestions.filter((item, pos, ary) => {
        // since we are sorted, we can just check if we match the item before us (based on messageId)
        const notDup = !pos || item.messageId !== ary[pos - 1].messageId;
        return notDup;
      });

      this.dateTracker[searchType] = {};

      // Update isStartOfDay for all items
      uniqueSuggestions.forEach(m => {
        m.isStartOfDay = this.isStartOfDay(m.createdOn, searchType);
      });

      this.suggestions.set(uniqueSuggestions);
    } catch (e) {
      this.error.set('ai_suggestions.errors.failed_to_load');
      this.chatLoggingService.error({
        logMessage: `chat-suggestion.component.fetchSuggestionMessages() ${searchType} fetch error for conversation ${this.conversationId}:: `,
        logParameters: [e]
      });
    } finally {
      this.fetchingMessages[searchType] = false;
      this.loading.set(false);
    }
  }

  public async searchMessage(parentMessageId: string, searchType: string): Promise<Message> {
    let retVal;
    try {
      this.chatLoggingService.info({
        logMessage: `chat-suggestion.component.searchMessage() searching ${parentMessageId} for ${searchType} type `,
        logParameters: [this.parentConversationId()]
      });
      const params: any = { conversationId: this.conversationId };
      params.skip = 0;
      params.top = 8;
      params.filter = searchType === 'active' ? "tag ne 'archived'" : "tag eq 'archived'";
      params.search = `${parentMessageId}`;
      const res = await this.chatCloudLinkService.chat.getMessages(params);
      const messageCollection = res.body;
      const messages = getItemsFromCollection(messageCollection);

      for (const message of messages) {
        const body = JSON.parse(message.body);
        if (body && body.parentMessageId === parentMessageId) {
          retVal = message;
          break;
        }
      }
    } catch (e) {
      this.chatLoggingService.error({
        logMessage: `chat-suggestion.component.searchMessage() Error searching ${parentMessageId} for ${searchType} type `,
        logParameters: [this.parentConversationId()]
      });
    }
    return retVal;
  }

  public async loadSuggestionAttachment(suggestion: any) {
    this.clearError();

    if (this.useStubData()) {
      // Stub mode: load mock attachment data
      this.loadStubAttachment(suggestion);
      return;
    }

    try {
      const response = await this.chatConversationService.getAttachmentAsBlob({
        conversationId: this.conversationId,
        attachmentId: suggestion.body.attachmentId
      });
      const fileReader = new FileReader();
      fileReader.onload = () => {
        const currentSuggestions = this.suggestions();
        const i: number = findIndex(
          currentSuggestions,
          (s: any) => s.body.attachmentId === suggestion.body.attachmentId
        );
        const suggest = JSON.parse(<any>fileReader.result);
        const sourceResponse = this.extendSuggestions(suggest);
        const updatedSuggestions = [...currentSuggestions];
        updatedSuggestions[i].suggestArticlesResponse = sourceResponse.suggestArticlesResponse;
        updatedSuggestions[i].suggestFaqAnswersResponse = sourceResponse.suggestFaqAnswersResponse;
        this.suggestions.set(updatedSuggestions);
      };
      fileReader.readAsText(response);
    } catch (e) {
      this.chatLoggingService.error({
        logMessage: 'chat-suggestions.component.loadSuggestionAttachment(): Error loading suggestion:',
        logParameters: [suggestion, ' ::', e]
      });
    }
  }

  public isStartOfDay(date: Date, type: string) {
    let retVal = false;
    try {
      const newDate: Date = new Date(date);

      const flatDate = `${newDate.getFullYear()}-${newDate.getMonth()}-${newDate.getDate()}`;
      if (!this.dateTracker[type][flatDate]) {
        this.dateTracker[type][flatDate] = flatDate;
        retVal = true;
      }
    } catch (ex) {
      this.chatLoggingService.error({
        logMessage: 'chat-suggestions.component.isStartOfDay(): Error',
        logParameters: [ex]
      });
    }

    return retVal;
  }

  public async archiveSuggestion(suggestionMessage: SuggestionMessage) {
    this.clearError();

    let url = SharedHelpers.createServiceUrl({
      service: 'gcp-gateway',
      version: '2018-05-27'
    });
    url = `${url}/conversations/${this.conversationId}/messages/${suggestionMessage.messageId}`;

    try {
      const body: any = suggestionMessage.body;
      const response: any = await this.httpClient
        .put(
          url,
          {
            projectId: body.gcpProjectId,
            body: {
              contentType: 'application/vnd.mitel.cc.suggestions.metadata',
              body: JSON.stringify(body),
              tag: 'archived'
            }
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: this.chatAuthService.token.access_token
            }
          }
        )
        .toPromise();
      const currentSuggestions = this.suggestions();
      const index = currentSuggestions.findIndex(s => s.messageId === response.messageId);
      if (index > -1) {
        const updatedSuggestions = [...currentSuggestions];
        updatedSuggestions[index].archived = true;
        this.suggestions.set(updatedSuggestions);
      }
    } catch (e) {
      this.error.set('ai_suggestions.errors.failed_to_archive');
      this.chatLoggingService.error({
        logMessage: 'chat-suggestions.component.archiveSuggestion(): Error',
        logParameters: [e]
      });
    }
  }

  public onTabIndexChange(event: any) {
    this.scrollIndexSubject.next(event);
  }

  public get activeSuggestions() {
    return this.suggestions();
  }

  public get archivedSuggestions() {
    return this.suggestions().filter(s => s.archived);
  }

  public async onSendSuggestion(data: any, suggestion: SuggestionMessage) {
    if (data) {
      try {
        if (data.action === 'sendArticle') {
          let uri = data.data.uri.replace(/ /g, '%20');
          uri = uri.replace('gs://', 'https://storage.googleapis.com/');
          await this.chatCloudLinkService.chat.createMessage({
            conversationId: this.parentConversationId(),
            body: {
              body: uri
            }
          });
        }

        if (data.action === 'sendSnippet') {
          await this.chatCloudLinkService.chat.createMessage({
            conversationId: this.parentConversationId(),
            body: {
              contentType: 'text/html',
              body: data.data.snippet
            }
          });
        }

        if (data.action === 'sendFaq') {
          await this.chatCloudLinkService.chat.createMessage({
            conversationId: this.parentConversationId(),
            body: {
              contentType: 'text/html',
              body: data.data.answer
            }
          });
        }
      } catch (e) {
        this.error.set('ai_suggestions.errors.failed_to_send');
        this.chatLoggingService.error({
          logMessage: 'chat-suggestions.component.onSendSuggestion(): Error',
          logParameters: [e]
        });
      }
    }
  }

  public onFeedback(data: any, suggestion: SuggestionMessage) {
    if (data) {
      const index = data.data.answerRecord.lastIndexOf('/');
      const answerId = data.data.answerRecord.substring(index + 1);

      this.sendFeedback(suggestion, answerId, data.answer);
    }
  }

  public async sendFeedback(suggestionMessage: SuggestionMessage, answerRecordId: string, answer: any) {
    try {
      await this.postSuggestionsFeedback(suggestionMessage, answerRecordId, answer);
    } catch (e) {
      if ((e.status && e.status.toString() === '401') || (e.statusCode && e.statusCode.toString() === '401')) {
        try {
          await this.chatAuthService.refreshToken();
          await this.postSuggestionsFeedback(suggestionMessage, answerRecordId, answer);
        } catch (e) {
          this.error.set('ai_suggestions.errors.failed_to_send_feedback');
          this.chatLoggingService.error({
            logMessage: 'chat-suggestions.component.sendFeedback(): Error',
            logParameters: [e]
          });
        }
      } else {
        this.error.set('ai_suggestions.errors.failed_to_send_feedback');
        this.chatLoggingService.error({
          logMessage: 'chat-suggestions.component.sendFeedback(): Error',
          logParameters: [e]
        });
      }
    }
  }

  private async postSuggestionsFeedback(suggestionMessage: SuggestionMessage, answerRecordId: string, answer: any) {
    this.clearError();
    const body: any = suggestionMessage.body;

    let url = SharedHelpers.createServiceUrl({
      service: 'gcp-gateway',
      version: '2018-05-27'
    });
    url = `${url}/conversations/${this.conversationId}/projects/${body.gcpProjectId}/feedback/${answerRecordId}`;
    await this.httpClient
      .post(
        url,
        {
          correctnessLevel: answer,
          mask: {
            paths: ['answer_feedback.correctness_level']
          }
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: this.chatAuthService.token.access_token
          }
        }
      )
      .toPromise();
  }

  public collapseAll() {
    const currentSuggestions = this.suggestions();
    const updatedSuggestions = currentSuggestions.map(suggestion => ({
      ...suggestion,
      articleExpanded: false,
      faqExpanded: false
    }));
    this.suggestions.set(updatedSuggestions);
  }

  public onOpened(source, suggestion) {
    if (source === 'article') {
      suggestion.articleExpanded = true;
    } else if (source === 'faq') {
      suggestion.faqExpanded = true;
    }

    setTimeout(() => {
      try {
        const pagerPart = this.elementInViewport(
          document.getElementById(suggestion.body.parentMessageId + '-active-' + source + 'Bottom')
        );
        const topPart = this.elementInViewport(document.getElementById(suggestion.body.parentMessageId + '-active'));
        if (!pagerPart || !topPart) {
          this.scrollingService.scrollToMessage(suggestion.body.parentMessageId + '-active');
        }
      } catch (e) {}
    }, 1000);
  }

  public isToday(date): boolean {
    const currentDate = new Date();
    const setDate = new Date(date);
    if (
      setDate.getDate() === currentDate.getDate() &&
      setDate.getMonth() === currentDate.getMonth() &&
      setDate.getFullYear() === currentDate.getFullYear()
    ) {
      return true;
    }
    return false;
  }

  public onBlurSuggestions() {
    this.isFocused.set(false);
  }

  public onFocusSuggestions() {
    this.isFocused.set(true);
    if (this.unReadMessages() > 0 && this.scrollingService.isNearBottom()) {
      this.unReadMessages.set(0);
    }
  }

  private elementInViewport(el) {
    const suggestionsMain = document.getElementById('suggestions-main');
    const mainBounding = suggestionsMain.getBoundingClientRect();
    const bounding = el.getBoundingClientRect();
    return (
      bounding.top >= mainBounding.top &&
      bounding.left >= mainBounding.left &&
      bounding.right <= mainBounding.right &&
      bounding.bottom <= mainBounding.bottom
    );
  }

  private extendSuggestions(suggestions) {
    const suggestSourceResponse = <any>{};

    if (suggestions && suggestions.humanAgentSuggestionResultsList) {
      for (const item of suggestions.humanAgentSuggestionResultsList) {
        if (item.suggestArticlesResponse && item.suggestArticlesResponse.articleAnswersList) {
          for (let index = 0; index < item.suggestArticlesResponse.articleAnswersList.length; index++) {
            item.suggestArticlesResponse.articleAnswersList[index].selected = index === 0 ? true : false;
          }
          item.suggestArticlesResponse.selectedIndex = 0;
          suggestSourceResponse.suggestArticlesResponse = item.suggestArticlesResponse;
        }
        if (item.suggestFaqAnswersResponse && item.suggestFaqAnswersResponse.faqAnswersList) {
          for (let index = 0; index < item.suggestFaqAnswersResponse.faqAnswersList.length; index++) {
            item.suggestFaqAnswersResponse.faqAnswersList[index].selected = index === 0 ? true : false;
          }
          item.suggestFaqAnswersResponse.selectedIndex = 0;
          suggestSourceResponse.suggestFaqAnswersResponse = item.suggestFaqAnswersResponse;
        }
      }
    }

    return suggestSourceResponse;
  }

  private setupScrolling(tab: string) {
    // setup scrolling
    let contentElement: ElementRef;
    contentElement = this.activesuggestionscontent;

    if (contentElement) {
      const domElement = contentElement.nativeElement as HTMLElement;

      if (this.scrollingService.scrollingContainer === domElement) {
        return;
      }
      this.scrollingService.setContainer(domElement);

      if (this.scrollingSubUp) {
        this.scrollingSubUp.unsubscribe();
      }
      if (this.scrollingSubDown) {
        this.scrollingSubDown.unsubscribe();
      }

      this.scrollingSubUp = this.scrollingService.getScrollUpObservable().subscribe(e => {
        try {
          this.scrollMoreUp(e, tab);
        } catch (ex) {
          // stifle exception to prevent unsubscribing
          this.chatLoggingService.error({
            logMessage: `chat-suggestion.component.setupScrolling(): Error in scrollingSubUp for ${tab} tab:`,
            logParameters: [ex]
          });
        }
      });
      this.subs.add(this.scrollingSubUp);

      this.scrollingSubDown = this.scrollingService.getScrollDownObservable().subscribe(e => {
        try {
          if (this.scrollingService.isNearBottom()) {
            this.unReadMessages.set(0);
          }
        } catch (ex) {
          // stifle exception to prevent unsubscribing
          this.chatLoggingService.error({
            logMessage: `chat-suggestion.component.setupScrolling(): Error in scrollingSubDown for ${tab} tab:`,
            logParameters: [ex]
          });
        }
      });
      this.subs.add(this.scrollingSubDown);
    } else {
      this.chatLoggingService.warn({
        logMessage: `chat-suggestion.component.setupScrolling()=> scrolling container not ready for ${tab} tab?`
      });
    }
  }

  private scrollMoreUp(event: any, tab: string) {
    if (this.moreMessages[tab] && !this.fetchingMessages[tab]) {
      this.fetchSuggestionMessages(tab);
    }
  }

  // ========== STUB DATA METHODS FOR UI TESTING ==========

  private async loadStubSuggestions(searchType: string) {
    this.chatLoggingService.info({ logMessage: '[STUB] Loading stub suggestions for:', logParameters: [searchType] });

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));

    const stubMessages = [
      {
        messageId: 'msg-001',
        body: JSON.stringify({
          parentMessageId: 'parent-msg-001',
          content: '<p>How do I reset my password?</p>',
          attachmentId: 'attach-001',
          gcpProjectId: 'project-123',
          suggestionMetaData: [
            { type: 'article', total: 3, recommended: true },
            { type: 'faq', total: 2, recommended: false }
          ]
        }),
        createdOn: new Date(Date.now() - 3600000), // 1 hour ago
        tag: searchType === 'archive' ? 'archived' : null
      },
      {
        messageId: 'msg-002',
        body: JSON.stringify({
          parentMessageId: 'parent-msg-002',
          content: '<p>What are your business hours?</p>',
          attachmentId: 'attach-002',
          gcpProjectId: 'project-123',
          suggestionMetaData: [
            { type: 'article', total: 2, recommended: false },
            { type: 'faq', total: 5, recommended: true }
          ]
        }),
        createdOn: new Date(Date.now() - 7200000), // 2 hours ago
        tag: null
      },
      {
        messageId: 'msg-003',
        body: JSON.stringify({
          parentMessageId: 'parent-msg-003',
          content: '<p>I need help with billing issues</p>',
          attachmentId: 'attach-003',
          gcpProjectId: 'project-123',
          suggestionMetaData: [{ type: 'article', total: 4, recommended: true }]
        }),
        createdOn: new Date(Date.now() - 86400000), // 1 day ago
        tag: null
      }
    ];

    const currentSuggestions = this.suggestions();
    const newStubSuggestions = stubMessages.map(m => {
      const body = JSON.parse(m.body);
      return {
        body: body,
        messageId: m.messageId,
        createdOn: m.createdOn,
        isStartOfDay: false,
        archived: m.tag && m.tag.toLowerCase() === 'archived' ? true : false,
        articleExpanded: false,
        faqExpanded: false
      };
    });

    const combinedSuggestions = [...currentSuggestions, ...newStubSuggestions];
    const sortedSuggestions = combinedSuggestions.sort((a, b) => {
      return new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime();
    });

    this.dateTracker[searchType] = {};
    sortedSuggestions.forEach(m => {
      m.isStartOfDay = this.isStartOfDay(m.createdOn, searchType);
    });

    this.suggestions.set(sortedSuggestions);

    this.moreMessages[searchType] = false; // No more messages in stub mode
  }

  private loadStubAttachment(suggestion: any) {
    this.chatLoggingService.info({
      logMessage: '[STUB] Loading stub attachment for:',
      logParameters: [suggestion.body.attachmentId]
    });

    const currentSuggestions = this.suggestions();
    const i: number = findIndex(currentSuggestions, (s: any) => s.body.attachmentId === suggestion.body.attachmentId);

    if (i === -1) return;

    // Create stub article and FAQ responses
    const stubSuggestData = {
      humanAgentSuggestionResultsList: [
        {
          suggestArticlesResponse: {
            articleAnswersList: [
              {
                title: 'Password Reset Guide',
                uri: 'gs://knowledge-base/articles/password-reset',
                snippetsList: [
                  '<p>To reset your password, click on the "Forgot Password" link on the login page.</p>',
                  '<p>You will receive an email with a reset link that expires in 24 hours.</p>'
                ],
                recommended: true,
                answerRecord: 'projects/project-123/answerRecords/answer-001'
              },
              {
                title: 'Account Security Best Practices',
                uri: 'gs://knowledge-base/articles/security',
                snippetsList: [
                  '<p>Use a strong password with at least 12 characters.</p>',
                  '<p>Enable two-factor authentication for added security.</p>'
                ],
                recommended: false,
                answerRecord: 'projects/project-123/answerRecords/answer-002'
              },
              {
                title: 'Managing Your Account',
                uri: 'gs://knowledge-base/articles/account-management',
                snippetsList: ['<p>You can update your profile settings in the Account section.</p>'],
                recommended: false,
                answerRecord: 'projects/project-123/answerRecords/answer-003'
              }
            ]
          },
          suggestFaqAnswersResponse: {
            faqAnswersList: [
              {
                question: 'How long does password reset take?',
                answer: '<p>Password reset emails are sent immediately and the link is valid for 24 hours.</p>',
                recommended: true,
                answerRecord: 'projects/project-123/answerRecords/faq-001'
              },
              {
                question: 'Can I use my old password again?',
                answer: '<p>No, you cannot reuse any of your last 5 passwords for security reasons.</p>',
                recommended: false,
                answerRecord: 'projects/project-123/answerRecords/faq-002'
              }
            ]
          }
        }
      ]
    };

    const sourceResponse = this.extendSuggestions(stubSuggestData);
    const updatedSuggestions = [...currentSuggestions];
    updatedSuggestions[i].suggestArticlesResponse = sourceResponse.suggestArticlesResponse;
    updatedSuggestions[i].suggestFaqAnswersResponse = sourceResponse.suggestFaqAnswersResponse;
    this.suggestions.set(updatedSuggestions);
  }
}
